/* eslint-disable class-methods-use-this */

import {Parameter} from '../../commons/parameterModel';
import {AtomParamTypesEnum, UninferredAtom} from '../expressionAtomModel';
import {PREVIOUS_RESULT_ID} from '../expressionModel';
import {buildEnumParameter, buildParameter, buildStringTemplateParameter} from '../expressionParamHelpers';
import {ExpressionsContext} from '../expressionsContext';
import {
  baseMethods,
  buildListResultOf,
  CompareMethod,
  InferredType,
  Method,
  MethodCategoriesEnum,
  StaticallyTypedMethod,
  StringListResult,
  StringResult,
  TagResult,
  TransformerIdsEnum,
} from './internal';

export class StringMethod extends StaticallyTypedMethod {
  constructor(category: MethodCategoriesEnum) {
    super(category, StringResult);
  }
}

export class StringModifier extends StringMethod {
  constructor() {
    super(MethodCategoriesEnum.TRANSFORM);
  }
}

export class SubstringExtractor extends StringMethod {
  constructor() {
    super(MethodCategoriesEnum.EXTRACT);
  }

  getParameters() {
    return [
      buildParameter('start', AtomParamTypesEnum.NUMBER),
      buildParameter('end', AtomParamTypesEnum.NUMBER),
    ];
  }

  getAtomDefaultParameters() {
    return {
      start: 0,
      end: 0,
    };
  }
}

export class StringTemplateMethod extends StaticallyTypedMethod {
  constructor(
    category: MethodCategoriesEnum,
    inferredType: InferredType,
    private paramName: string,
    private canUsePreviousResult?: boolean,
  ) {
    super(category, inferredType);
  }

  getParameters(): ReadonlyArray<Parameter> {
    return [
      buildStringTemplateParameter(this.paramName, {
        canUsePreviousResult: this.canUsePreviousResult,
      }),
    ];
  }

  getAtomDefaultParameters(context?: ExpressionsContext) {
    if (this.canUsePreviousResult) {
      return {
        [this.paramName]: {template: [{expression: PREVIOUS_RESULT_ID}]},
      };
    }

    return super.getAtomDefaultParameters(context);
  }
}

export enum OccurrencesEnum {
  FIRST = 'first',
  ALL = 'all',
}

export class Extractor extends Method {
  constructor(private type: InferredType) {
    super(MethodCategoriesEnum.EXTRACT);
  }

  inferType(atom: UninferredAtom) {
    return atom.params.occurrences === OccurrencesEnum.FIRST ? this.type : buildListResultOf(this.type);
  }

  getParameters(): ReadonlyArray<Parameter> {
    return [buildEnumParameter('occurrences', OccurrencesEnum)];
  }
}

class TextExtractor extends Extractor {
  constructor() {
    super(StringResult);
  }

  getParameters() {
    return [
      ...super.getParameters(),
      buildStringTemplateParameter('pattern', {canUseRegex: true}),
      buildStringTemplateParameter('before', {canUseRegex: true}),
      buildStringTemplateParameter('after', {canUseRegex: true}),
    ];
  }
}

export enum SplitSeparatorsEnum {
  LINE = 'line',
  COMMA = 'comma',
  SEMICOLON = 'semicolon',
  WHITESPACE = 'whitespace',
  CUSTOM_SPLITTER = 'custom_splitter',
}

export class SplitString extends StaticallyTypedMethod {
  constructor() {
    super(MethodCategoriesEnum.TRANSFORM, StringListResult);
  }

  getParameters() {
    return [
      buildEnumParameter('separator', SplitSeparatorsEnum),
      buildParameter('custom_splitter', AtomParamTypesEnum.STRING),
    ];
  }
}

export class TagConverter extends StringTemplateMethod {
  constructor() {
    super(MethodCategoriesEnum.CONVERT, TagResult, 'tag', true);
  }

  getParameters() {
    return [
      ...super.getParameters(),
      buildParameter('parent_tag', AtomParamTypesEnum.NUMBER, 'tag'),
      buildParameter('is_visible_in_p2', AtomParamTypesEnum.BOOLEAN),
      buildParameter('force_create_tag', AtomParamTypesEnum.BOOLEAN),
    ];
  }

  getAtomDefaultParameters(context?: ExpressionsContext) {
    return {
      ...super.getAtomDefaultParameters(context),
      force_create_tag: false,
    };
  }
}

enum StringComparatorsEnum {
  EQUALS = 'equals',
  NOT_EQUAL = 'not_equal',
  STARTS_WITH = 'starts_with',
  ENDS_WITH = 'ends_with',
  CONTAINS = 'contains',
  NOT_CONTAIN = 'not_contain',
}

class StringCompareMethod extends CompareMethod {
  constructor() {
    super(StringComparatorsEnum);
  }

  getParameters() {
    const valueParam = buildStringTemplateParameter('value', {canUseRegex: true});

    return [buildEnumParameter('operation', StringComparatorsEnum), valueParam];
  }
}

export const baseStringMethods: Partial<Record<TransformerIdsEnum, Method>> = {
  to_uppercase: new StringModifier(),
  to_lowercase: new StringModifier(),
  substring: new SubstringExtractor(),
  extract_text: new TextExtractor(),
  compare: new StringCompareMethod(),
  ...baseMethods,
};
